
import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ArrowLeft, User, Mail, Calendar, TrendingUp, Edit2, Check, X, Shield, AlertTriangle } from "lucide-react"
import { useAuth } from "@/lib/auth/auth-context"
import { useTransactions } from "@/lib/context/transaction-context"
import { toast } from "sonner"
import { format } from "date-fns"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface ProfileProps {
  onBack: () => void
}

export function Profile({ onBack }: ProfileProps) {
  const { user, updateProfile, updatePassword, deleteAccount } = useAuth()
  const { transactions } = useTransactions()
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState(user?.user_metadata?.full_name || "")
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  const getUserInitials = () => {
    if (!user) return "U"
    const displayName = user.user_metadata?.full_name || user.email || "User"
    return displayName
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const getMemberSince = () => {
    if (!user?.created_at) return "Unknown"
    return format(new Date(user.created_at), "MMMM yyyy")
  }

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty")
      return
    }

    setIsSaving(true)
    const { error } = await updateProfile(name.trim())
    setIsSaving(false)

    if (error) {
      toast.error(`Failed to update profile: ${error.message}`)
    } else {
      toast.success("Profile updated successfully")
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setName(user?.user_metadata?.full_name || "")
    setIsEditing(false)
  }

  const handleChangePassword = () => {
    setShowPasswordDialog(true)
  }

  const handlePasswordSubmit = async () => {
    // Strong password validation
    const { validatePassword } = await import("@/lib/utils/password-validation")
    const validation = validatePassword(newPassword)

    if (!validation.isValid) {
      toast.error(validation.errors[0]) // Show first error
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match")
      return
    }

    setIsChangingPassword(true)
    const { error } = await updatePassword(newPassword)
    setIsChangingPassword(false)

    if (error) {
      toast.error(`Failed to update password: ${error.message}`)
    } else {
      toast.success("Password updated successfully")
      setShowPasswordDialog(false)
      setNewPassword("")
      setConfirmPassword("")
    }
  }

  const handlePasswordDialogClose = (open: boolean) => {
    setShowPasswordDialog(open)
    if (!open) {
      setNewPassword("")
      setConfirmPassword("")
    }
  }

  const handlePasswordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isChangingPassword) {
      e.preventDefault()
      handlePasswordSubmit()
    }
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true)
    const { error } = await deleteAccount()
    setIsDeleting(false)

    if (error) {
      toast.error(`Failed to delete account: ${error.message}`)
    } else {
      toast.success("Account deleted successfully")
    }
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-md space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Profile</h1>
            <p className="text-sm text-muted-foreground">Manage your account</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-24 w-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl font-bold">
                {getUserInitials()}
              </div>
              <div>
                <h2 className="text-3xl font-bold">
                  {user?.user_metadata?.full_name || user?.email?.split("@")[0] || "User"}
                </h2>
                <p className="text-sm opacity-90 mt-1">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Account Information
            </CardTitle>
            <CardDescription>Your personal details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="flex gap-2">
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!isEditing}
                  placeholder="Enter your name"
                />
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      className="text-green-600 hover:text-green-700"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <div className="flex gap-2">
                <Input value={user?.email || ""} disabled />
                <Button variant="outline" size="icon" disabled>
                  <Mail className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Statistics
            </CardTitle>
            <CardDescription>Your account activity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-semibold">{getMemberSince()}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Transactions</p>
                  <p className="font-semibold">{transactions.length}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={handleChangePassword}
            >
              <Shield className="h-4 w-4" />
              Change Password
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 text-destructive hover:bg-destructive/10"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Delete Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your account
                    and remove all your data from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {isDeleting ? "Deleting..." : "Delete Account"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Password Change Dialog */}
        <AlertDialog open={showPasswordDialog} onOpenChange={handlePasswordDialogClose}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Password</AlertDialogTitle>
              <AlertDialogDescription>
                Enter your new password below. It must be at least 12 characters with uppercase, lowercase, number, and special character.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={handlePasswordKeyDown}
                  placeholder="Enter new password"
                  disabled={isChangingPassword}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={handlePasswordKeyDown}
                  placeholder="Confirm new password"
                  disabled={isChangingPassword}
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isChangingPassword}>Cancel</AlertDialogCancel>
              <Button
                onClick={handlePasswordSubmit}
                disabled={isChangingPassword}
              >
                {isChangingPassword ? "Updating..." : "Update Password"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
